import { Plus } from "lucide-react";
import React, { useEffect } from "react";

import { IgPaper, IgTypography } from "@intelligems/ig-theme";
import { CircularProgress, Grid, useTheme } from "@mui/material";
import { fromGqlId } from "@src/api/formatters/shopify";
import { AddAnotherButton } from "@src/components/Utils/AddAnotherButton";
import { CustomEventProductPickerWrapper } from "@src/features/customEvents/components/subpages/CustomEventProductPickerWrapper";
import { ProductListItem } from "@src/features/customEvents/components/subpages/ProductListItem";
import { useDispatch, useSelector } from "@src/store";
import {
  getCustomerEventSelectedProducts,
  getCustomerEventSelectedProductsLoading,
  removeCustomEventProduct,
} from "@src/store/slices/customEvents";
import { loadCustomEventSelectedProducts } from "@src/store/slices/customEvents/thunks/loadCustomEventSelectedProducts";
import { isProductPickerOpen } from "@src/store/slices/productPicker/selectors/isProductPickerOpen";
import { openCustomEventsProductPicker } from "@src/store/slices/productPicker/thunks/customEvents/openCustomEventsProductPicker";

interface ProductPageViewProps {
  isArchived: boolean;
}

export const ProductPageView = ({ isArchived }: ProductPageViewProps) => {
  const dispatch = useDispatch();
  const productPickerOpen = useSelector(isProductPickerOpen("customEvents"));
  const selectedProducts = useSelector(getCustomerEventSelectedProducts);
  const selectedProductsLoading = useSelector(
    getCustomerEventSelectedProductsLoading
  );
  const theme = useTheme();
  useEffect(() => {
    void dispatch(loadCustomEventSelectedProducts());
  }, []);

  if (selectedProductsLoading) {
    return (
      <Grid
        mt={5}
        container
        rowSpacing={1}
        direction={"column"}
        justifyContent={"center"}
        textAlign={"center"}
      >
        <Grid item>
          <IgTypography>Loading Products...</IgTypography>
        </Grid>
        <Grid item>
          <CircularProgress size={25} />
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container marginTop={"15px"}>
      {!productPickerOpen && selectedProducts.length === 0 && (
        <IgPaper
          sx={{
            boxShadow: "none",
            borderRadius: "5px",
            "&:hover": {
              cursor: "pointer",
              border: `1px solid ${theme.igBorders.accent.default}`,
              backgroundColor: "#F8FAFF",
            },
            width: "100%",
          }}
          onClick={() => void dispatch(openCustomEventsProductPicker())}
        >
          <Grid
            container
            direction={"column"}
            alignItems={"center"}
            paddingLeft={"40px"}
            paddingRight={"40px"}
            paddingTop={"10px"}
            paddingBottom={"10px"}
            width={"100%"}
          >
            <Grid item>
              <Plus size={36} />
            </Grid>
            <Grid item>
              <IgTypography>Add Products</IgTypography>
            </Grid>
          </Grid>
        </IgPaper>
      )}

      {/* Selected Products List */}
      <Grid container direction={"column"} marginTop={"10px"}>
        {selectedProducts.map((product) => (
          <ProductListItem
            key={product.id}
            product={product}
            isArchived={isArchived}
            onRemove={() => {
              dispatch(
                removeCustomEventProduct({ productId: fromGqlId(product.id) })
              );
            }}
          />
        ))}
      </Grid>
      {selectedProducts.length > 0 && !isArchived && (
        <AddAnotherButton
          onAddClick={() => void dispatch(openCustomEventsProductPicker())}
          itemLabel={"add another product"}
        />
      )}
      {productPickerOpen && (
        <Grid item>
          <CustomEventProductPickerWrapper />
        </Grid>
      )}
    </Grid>
  );
};
